import { API_BASE_URL } from './config';
import { apiFetch, getAuthToken } from './apiFetch';

export type ParsedTraining = {
  code: string;
  name: string;
  description: string;
  provider: 'internal' | 'external';
  format: 'online' | 'offline' | 'blended';
  durationHours: number | null;
  competenciesImproved: Array<{ competencyCode: string; levelGainEstimate: number }>;
};

export type ParsedMilestoneTemplate = {
  name: string;
  milestone30: string;
  milestone90: string;
  milestone180: string;
};

export type ParsedEmployee = {
  name: string;
  email: string;
  role: 'employee' | 'manager' | 'hr' | 'admin';
  positionCode: string | null;
  divisionName: string | null;
};

export type ParsedMetricSnapshot = {
  employeeEmail: string;
  metricCode: string;
  value: number;
  capturedAt: string | null;
};

export type ParsedDocumentData =
  | { type: 'training_catalogue'; trainings: ParsedTraining[] }
  | { type: 'milestone_templates'; milestones: ParsedMilestoneTemplate[] }
  | { type: 'employee_roster'; employees: ParsedEmployee[] }
  | { type: 'metric_data'; snapshots: ParsedMetricSnapshot[] };

export type ParseStatus = 'idle' | 'pending' | 'done' | 'error';

export type DocumentValidation = {
  isHRDocument: boolean;
  category: string | null;
  confidence: 'high' | 'medium' | 'low';
  matchesUserSelection: boolean;
  reason: string;
};

export type FileUploadDto = {
  id: string;
  companyId: string;
  fileName: string;
  fileSize?: number;
  mimeType?: string;
  entityType?: string;
  entityId?: string;
  hasExtractedText?: boolean;
  extractedText?: string;
  detectedCategory?: string | null;
  availableCategories?: string[];
  parseableTypes?: string[];
  validation?: DocumentValidation | null;
  // Structured extraction status (set by POST /parse, polled via GET /parse-status)
  isParseable?: boolean;
  parseStatus?: ParseStatus;
  parsedData?: ParsedDocumentData | null;
  parseError?: string | null;
  createdAt: string;
};

export const DOCUMENT_ENTITY_TYPE_LABELS: Record<string, string> = {
  competency_framework: 'Competency Framework',
  job_description: 'Job Description',
  performance_review: 'Performance Review',
  handbook: 'Employee Handbook / SOP',
  training_catalogue: 'Training Catalogue',
  career_framework: 'Career Development Framework',
  nine_box_criteria: '9-Box Grid Criteria',
  data_dictionary: 'Data Dictionary / Score Formulas',
  employee_roster: 'Employee Roster (Bulk Import)',
  metric_data: 'Metric Data (Bulk Import)',
};

/**
 * Upload an actual file (PDF/DOCX/txt) — multipart/form-data.
 * Server will parse the file to text and store in MinIO.
 */
export async function uploadFile(
  input: {
    companyId: string;
    uploadedByUserId?: string;
    entityType?: string;
    entityId?: string;
    file: File;
    forceUpload?: boolean;   // bypass AI content validation (user override)
  },
) {
  const form = new FormData();
  form.append('file', input.file);
  form.append('companyId', input.companyId);
  if (input.uploadedByUserId) form.append('uploadedByUserId', input.uploadedByUserId);
  if (input.entityType) form.append('entityType', input.entityType);
  if (input.entityId) form.append('entityId', input.entityId);
  if (input.forceUpload) form.append('forceUpload', 'true');

  const res = await apiFetch(`${API_BASE_URL}/api/files/upload`, {
    method: 'POST',
    body: form,
  });
  return res.json() as Promise<{
    success: boolean;
    code?: string;                      // 'not_hr_document' when blocked
    validation?: DocumentValidation;    // AI validation result (blocking or warning)
    data?: { file: FileUploadDto };
    error?: string;
  }>;
}

/** Upload a text document (legacy / text-only path). */
export async function uploadTextDocument(
  input: {
    companyId: string;
    uploadedByUserId?: string;
    entityType?: string;
    entityId?: string;
    fileName: string;
    mimeType?: string;
    rawText: string;
  },
) {
  const res = await apiFetch(`${API_BASE_URL}/api/files/upload-text`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  return res.json() as Promise<{
    success: boolean;
    data?: { file: FileUploadDto };
    error?: string;
  }>;
}

export async function listFiles(
  companyId: string,
  options?: { entityType?: string; entityId?: string },
) {
  const params = new URLSearchParams();
  if (options?.entityType) params.set('entityType', options.entityType);
  if (options?.entityId) params.set('entityId', options.entityId);
  const qs = params.toString() ? `?${params.toString()}` : '';
  const res = await apiFetch(
    `${API_BASE_URL}/api/files/company/${companyId}${qs}`,
    { headers: { 'Content-Type': 'application/json' } },
  );
  return res.json() as Promise<{
    success: boolean;
    data?: { files: FileUploadDto[] };
    error?: string;
  }>;
}

/**
 * Build a stream URL for a file — use with window.open() for browser downloads.
 * The server streams the file bytes directly, bypassing the internal MinIO hostname.
 * A JWT token is appended as a query param (required since window.open can't send headers).
 */
export function getFileStreamUrl(fileId: string, companyId: string): string | null {
  const token = getAuthToken();
  if (!token) return null;
  return `${API_BASE_URL}/api/files/${fileId}/stream?companyId=${encodeURIComponent(companyId)}&token=${encodeURIComponent(token)}`;
}

export async function deleteFile(fileId: string, companyId: string) {
  const res = await apiFetch(`${API_BASE_URL}/api/files/${fileId}?companyId=${companyId}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
  });
  return res.json() as Promise<{ success: boolean; error?: string }>;
}

export async function triggerDocumentParse(fileId: string, companyId: string) {
  const res = await apiFetch(`${API_BASE_URL}/api/files/${fileId}/parse`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ companyId }),
  });
  return res.json() as Promise<{
    success: boolean;
    data?: { queued: boolean; reason?: string };
    error?: string;
  }>;
}

export async function getDocumentParseStatus(fileId: string, companyId: string) {
  const res = await apiFetch(
    `${API_BASE_URL}/api/files/${fileId}/parse-status?companyId=${companyId}`,
    { headers: { 'Content-Type': 'application/json' } },
  );
  return res.json() as Promise<{
    success: boolean;
    data?: {
      fileId: string;
      isParseable: boolean;
      hasText: boolean;
      status: ParseStatus;
      parsedData: ParsedDocumentData | null;
      error: string | null;
      startedAt: string | null;
      completedAt: string | null;
    };
    error?: string;
  }>;
}

export async function importTrainings(companyId: string, trainings: ParsedTraining[]) {
  const res = await apiFetch(`${API_BASE_URL}/api/training-catalogue/import`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ companyId, trainings }),
  });
  return res.json() as Promise<{
    success: boolean;
    data?: { inserted: number; updated: number; skipped: number; conflicts: Array<{ code: string; reason: string }> };
    error?: string;
  }>;
}

export async function importEmployeeRoster(companyId: string, employees: ParsedEmployee[]) {
  const res = await apiFetch(`${API_BASE_URL}/api/users/import-roster`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ companyId, employees }),
  });
  return res.json() as Promise<{
    success: boolean;
    data?: { created: number; skipped: number; errors: Array<{ email: string; reason: string }> };
    error?: string;
  }>;
}

export async function importMetricSnapshots(companyId: string, snapshots: ParsedMetricSnapshot[]) {
  const res = await apiFetch(`${API_BASE_URL}/api/metrics/import-snapshots`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ companyId, snapshots }),
  });
  return res.json() as Promise<{
    success: boolean;
    data?: { created: number; skipped: number; errors: Array<{ row: string; reason: string }> };
    error?: string;
  }>;
}

export async function importMilestoneTemplates(companyId: string, templates: ParsedMilestoneTemplate[]) {
  const res = await apiFetch(`${API_BASE_URL}/api/milestone-templates/import`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ companyId, templates }),
  });
  return res.json() as Promise<{
    success: boolean;
    data?: { inserted: number; skipped: number };
    error?: string;
  }>;
}
